import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    BottomSheet,
    Separator,
    TextInput,
    CheckBox,
    Avatar,
    Button,
    Switch,
    Item,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    ChevronLeft,
    Delete,
    Search
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

type Recipient = {
    iban: string;
    id: string;
};

const ChevronLeftIcon = createKitIcon(ChevronLeft);

const DeleteIcon = createKitIcon(Delete);

const SearchIcon = createKitIcon(Search);

const DEFAULT_RECIPIENT: Recipient = {
    iban: "TR33 0006 1005 1978 6457 8413 26",
    id: "mert"
};

const RECIPIENTS: Array<Recipient> = [
    DEFAULT_RECIPIENT,
    {
        iban: "TR58 0001 2009 4520 0058 0012 77",
        id: "zeynep"
    },
    {
        iban: "TR12 0004 6007 8800 0006 1200 41",
        id: "can"
    },
    {
        iban: "TR90 0010 0099 9901 2345 6780 05",
        id: "ada"
    },
    {
        iban: "TR27 0013 4000 0023 4567 8901 62",
        id: "defne"
    }
];

const KEYS = [
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "",
    "0",
    "delete"
] as const;

const BALANCE = 48215.4;

const INITIAL_AMOUNT = 1250;

const MAX_AMOUNT = 999999;

const stylesheet = StyleSheet.create({
    recents: {
        justifyContent: "space-between",
        flexDirection: "row"
    },
    summary: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    favorite: {
        justifyContent: "space-between",
        flexDirection: "row"
    },
    column: {
        flexDirection: "column"
    },
    recent: {
        alignItems: "center",
        flex: 1
    },
    header: {
        alignItems: "center",
        flexDirection: "row"
    },
    keypad: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    amount: {
        alignItems: "center"
    },
    screen: {
        alignSelf: "center",
        width: "100%",
        flexGrow: 1
    }
});

const handlePress = () => undefined;

const Mobile02 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        controlHeights,
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        selectedId,
        setSelectedId
    ] = useState("mert");

    const [
        amount,
        setAmount
    ] = useState(INITIAL_AMOUNT);

    const [
        isSheetOpen,
        setIsSheetOpen
    ] = useState(true);

    const [
        isFavorite,
        setIsFavorite
    ] = useState(true);

    const [
        note,
        setNote
    ] = useState(t("blocks.mobile02.noteValue"));

    const currency = new Intl.NumberFormat(activeLocale, {
        minimumFractionDigits: 2,
        style: "currency",
        currency: "TRY"
    });

    const selected = RECIPIENTS.find((recipient) => recipient.id === selectedId) ?? DEFAULT_RECIPIENT;

    const selectedName = t(`blocks.mobile02.people.${selected.id}`);

    const maskedIban = `${selected.iban.slice(0, 4)} •••• •••• ${selected.iban.slice(-7)}`;

    const columnWidth = `${100 / 3}%` as const;

    const handleKey = (key: string) => () => {
        if(key === "delete") {
            setAmount(Math.floor(amount / 10));

            return;
        }

        const next = amount * 10 + Number(key);

        if(next <= MAX_AMOUNT) {
            setAmount(next);
        }
    };

    const handleOpen = () => setIsSheetOpen(true);

    const handleClose = () => setIsSheetOpen(false);

    const renderRecent = (recipient: Recipient) => <View
        style={[
            stylesheet.recent,
            {
                gap: spaces.spacingXs
            }
        ]}
        key={recipient.id}
    >
        <Avatar
            accessibilityLabel={t(`blocks.mobile02.people.${recipient.id}`)}
            title={t(`blocks.mobile02.people.${recipient.id}`)}
            onPress={() => setSelectedId(recipient.id)}
            isShowBorder={selectedId === recipient.id}
            size="small"
        />
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {t(`blocks.mobile02.people.${recipient.id}`).split(" ")[0]}
        </Text>
    </View>;

    const renderKey = (key: typeof KEYS[number], index: number) => <View
        style={{
            padding: spaces.spacingXxs,
            width: columnWidth
        }}
        key={`${key}${index}`}
    >
        {
            key === "" ?
                <View
                    style={{
                        height: controlHeights.large
                    }}
                />
            :
                <Button
                    accessibilityLabel={key === "delete" ? t("blocks.mobile02.delete") : key}
                    icon={key === "delete" ? DeleteIcon : undefined}
                    title={key === "delete" ? undefined : key}
                    spreadBehaviour="stretch"
                    onPress={handleKey(key)}
                    variant="ghost"
                    type="neutral"
                    size="large"
                />
        }
    </View>;

    const renderSummary = (label: string, value: string) => <View
        style={stylesheet.column}
        key={label}
    >
        <Separator/>
        <View
            style={[
                stylesheet.summary,
                {
                    paddingVertical: spaces.spacingSm
                }
            ]}
        >
            <Text
                variant="bodyMediumSize"
                color="mid"
            >
                {label}
            </Text>
            <Text
                variant="labelMediumSize"
                color="high"
            >
                {value}
            </Text>
        </View>
    </View>;

    const renderSheetFooter = () => <View
        style={[
            stylesheet.column,
            {
                gap: spaces.spacingSm
            }
        ]}
    >
        <Button
            title={t("blocks.mobile02.send", [
                currency.format(amount)
            ])}
            spreadBehaviour="stretch"
            onPress={handleClose}
            variant="filled"
            type="primary"
            size="large"
        />
        <Button
            title={t("blocks.mobile02.cancel")}
            spreadBehaviour="stretch"
            onPress={handleClose}
            variant="ghost"
            type="neutral"
            size="large"
        />
    </View>;

    return <ScrollView
        contentContainerStyle={[
            stylesheet.screen,
            {
                maxWidth: width > shapeConfigs.sheetMaxWidth ? shapeConfigs.sheetMaxWidth : undefined,
                padding: spaces.spacingMd,
                gap: spaces.spacingLg
            }
        ]}
    >
        <View
            style={[
                stylesheet.header,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Button
                accessibilityLabel={t("blocks.mobile02.back")}
                icon={ChevronLeftIcon}
                onPress={handlePress}
                variant="ghost"
                type="neutral"
            />
            <Text
                variant="titleLargeSize"
                color="high"
            >
                {t("blocks.mobile02.title")}
            </Text>
        </View>
        <TextInput
            accessibilityLabel={t("blocks.mobile02.searchLabel")}
            placeholder={t("blocks.mobile02.search")}
            spreadBehaviour="stretch"
            icon={SearchIcon}
        />
        <View
            style={[
                stylesheet.column,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Text
                variant="labelMediumSize"
                color="mid"
            >
                {t("blocks.mobile02.recents")}
            </Text>
            <View
                style={stylesheet.recents}
            >
                {RECIPIENTS.map(renderRecent)}
            </View>
        </View>
        <Item
            onPress={handlePress}
            isSelected={true}
            variant="filled"
        >
            <Item.Media
                avatarProps={{
                    title: selectedName
                }}
                variant="avatar"
            />
            <Item.Content>
                <Item.Title>
                    {selectedName}
                </Item.Title>
                <Item.Description>
                    {maskedIban}
                </Item.Description>
            </Item.Content>
            <Item.Actions>
                <CheckBox
                    accessibilityLabel={selectedName}
                    onPress={handlePress}
                    isChecked={true}
                />
            </Item.Actions>
        </Item>
        <View
            style={[
                stylesheet.amount,
                {
                    paddingVertical: spaces.spacingMd,
                    gap: spaces.spacingXs
                }
            ]}
        >
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.mobile02.amountLabel")}
            </Text>
            <Text
                variant="displaySmallSize"
                color="high"
            >
                {currency.format(amount)}
            </Text>
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.mobile02.balance", [
                    currency.format(BALANCE)
                ])}
            </Text>
        </View>
        <View
            style={stylesheet.keypad}
        >
            {KEYS.map(renderKey)}
        </View>
        <Button
            title={t("blocks.mobile02.continue")}
            spreadBehaviour="stretch"
            onPress={handleOpen}
            variant="filled"
            type="primary"
            size="large"
        />
        <BottomSheet
            description={t("blocks.mobile02.sheetDescription")}
            title={t("blocks.mobile02.sheetTitle")}
            renderBottom={renderSheetFooter}
            isWorkWithAnimation={true}
            onClosed={handleClose}
            isOpen={isSheetOpen}
            isAutoHeight={true}
        >
            <View
                style={[
                    stylesheet.column,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Item
                    variant="plain"
                >
                    <Item.Media
                        avatarProps={{
                            title: selectedName
                        }}
                        variant="avatar"
                    />
                    <Item.Content>
                        <Item.Title>
                            {selectedName}
                        </Item.Title>
                        <Item.Description>
                            {selected.iban}
                        </Item.Description>
                    </Item.Content>
                </Item>
                <View
                    style={stylesheet.column}
                >
                    {renderSummary(t("blocks.mobile02.summary.amount"), currency.format(amount))}
                    {renderSummary(t("blocks.mobile02.summary.fee"), currency.format(0))}
                    {renderSummary(t("blocks.mobile02.summary.arrival"), t("blocks.mobile02.summary.instant"))}
                </View>
                <Switch
                    subTitle={t("blocks.mobile02.favoriteHint")}
                    title={t("blocks.mobile02.favorite")}
                    onCheckedChange={setIsFavorite}
                    spreadBehaviour="stretch"
                    isChecked={isFavorite}
                    isFlip={true}
                />
                <TextInput
                    title={t("blocks.mobile02.note")}
                    spreadBehaviour="stretch"
                    onChangeText={setNote}
                    value={note}
                />
            </View>
        </BottomSheet>
    </ScrollView>;
};
export default Mobile02;
